import {
  ChatAction,
  ChatActivityStatus,
  ChatFile,
} from '../services/chatbot-ia.service';

export const IMAGE_GENERATION_STATUS_LABEL = 'Gerando imagem…';
export const IMAGE_LOADING_STATUS_LABEL = 'Carregando imagem…';

const LEGACY_GENERATED_IMAGE_LABELS: Record<string, string> = {
  'Capa gerada': 'Imagem gerada',
};

/** Rótulo do overlay — neutro (não amarra ao blog). Normaliza bolhas antigas. */
export function formatGeneratedImageLabel(label?: string | null): string {
  const trimmed = label?.trim();
  if (!trimmed) return 'Imagem gerada';
  return LEGACY_GENERATED_IMAGE_LABELS[trimmed] ?? trimmed;
}

const IMAGE_GENERATION_TOOL = 'admin_generate_blog_image_ai';

function isImageGenerationStep(
  step: ChatActivityStatus | null | undefined
): boolean {
  return step?.toolName === IMAGE_GENERATION_TOOL && step.phase === 'tool';
}

function urlKeys(url: string | undefined): string[] {
  const raw = String(url ?? '').trim();
  if (!raw) return [];
  const keys = new Set<string>([raw]);
  try {
    if (/^https?:\/\//i.test(raw)) {
      const parsed = new URL(raw);
      keys.add(parsed.pathname.replace(/^\//, ''));
    } else {
      keys.add(raw.replace(/^\//, ''));
    }
  } catch {
    /* ignore */
  }
  return [...keys];
}

export function urlsReferToSameAsset(
  left: string | undefined,
  right: string | undefined
): boolean {
  const a = urlKeys(left);
  const b = urlKeys(right);
  if (!a.length || !b.length) return false;
  return a.some((x) => b.some((y) => x === y || x.endsWith(y) || y.endsWith(x)));
}

export function filterActionsForChatFiles(
  actions: ChatAction[] | undefined,
  files: ChatFile[] | undefined
): ChatAction[] {
  if (!actions?.length) return [];
  const imageFiles = (files ?? []).filter(
    (file) =>
      file.kind === 'generated_image' ||
      Boolean(file.mimeType?.startsWith('image/'))
  );
  if (!imageFiles.length) return actions;

  return actions.filter((action) => {
    if (!action.url) return true;
    if (/acessar\s+imagem/i.test(action.label)) return false;
    return !imageFiles.some((file) => urlsReferToSameAsset(action.url, file.url));
  });
}

export function compactGeneratedImageReply(
  text: string,
  files: ChatFile[] | undefined
): string {
  if (!files?.some((file) => file.kind === 'generated_image')) {
    return text;
  }

  let next = text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, (full, label: string, url: string) => {
      if (/acessar\s+imagem/i.test(label)) return '';
      if (/\.(png|jpe?g|webp|gif)(\?|#|$)/i.test(url)) return '';
      const generated = files.find((file) => file.kind === 'generated_image');
      if (generated && urlsReferToSameAsset(url, generated.url)) return '';
      return label;
    })
    .replace(/^\s*Acessar [Ii]magem\s*$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  if (!next) return 'Imagem pronta.';

  const sentence = next.match(/^[^.!?\n…]+[.!?…]?/)?.[0]?.trim() ?? next;
  return sentence;
}

export function resolveImageGenerationActivity(
  process: ChatActivityStatus[] | undefined,
  currentActivity?: ChatActivityStatus | null
): ChatActivityStatus {
  if (isImageGenerationStep(currentActivity)) {
    return currentActivity!;
  }

  if (process?.length) {
    for (let index = process.length - 1; index >= 0; index -= 1) {
      const step = process[index];
      if (isImageGenerationStep(step)) return step;
    }
  }

  return {
    phase: 'tool',
    toolName: IMAGE_GENERATION_TOOL,
    label: IMAGE_GENERATION_STATUS_LABEL,
  };
}

export function isImageGenerationInProgress(
  process: ChatActivityStatus[] | undefined,
  files: ChatFile[] | undefined,
  currentActivity?: ChatActivityStatus | null
): boolean {
  if (files?.some((file) => file.kind === 'generated_image')) return false;

  if (isImageGenerationStep(currentActivity)) return true;
  if (!process?.length) return false;

  let pending = false;
  for (const step of process) {
    if (step.toolName !== IMAGE_GENERATION_TOOL) continue;
    if (step.phase === 'tool') pending = true;
    if (step.phase === 'tool_done') pending = false;
  }
  return pending;
}

/** Tool rodando mas o card `files[]` ainda não chegou no widget. */
export function isImageToolAwaitingFile(
  process: ChatActivityStatus[] | undefined,
  files: ChatFile[] | undefined,
  currentActivity?: ChatActivityStatus | null
): boolean {
  return isImageGenerationInProgress(process, files, currentActivity);
}

export function isGeneratedImageFile(file: ChatFile | null | undefined): boolean {
  if (!file) return false;
  return (
    file.kind === 'generated_image' ||
    Boolean(file.mimeType?.startsWith('image/'))
  );
}
